import { useEffect, useState } from "react";
import { Search, Download, X } from "lucide-react";
import api, { formatPrice, apiError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUSES = ["pending", "confirmed", "processing", "shipped", "out_for_delivery", "delivered", "cancelled"];

export default function AdminOrders() {
    const [orders, setOrders] = useState([]);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [selected, setSelected] = useState(null);

    const load = () => {
        const sp = new URLSearchParams();
        if (q) sp.set("q", q);
        if (statusFilter) sp.set("status", statusFilter);
        api.get(`/admin/orders?${sp}`).then(({ data }) => setOrders(data.orders));
    };
    useEffect(() => { load(); }, [statusFilter]);

    const updateStatus = async (order_number, status) => {
        try { const { data } = await api.put(`/admin/orders/${order_number}/status`, { status }); setSelected(data.order); load(); toast.success("Status updated"); }
        catch (err) { toast.error(apiError(err)); }
    };

    const saveTracking = async (order_number, tracking) => {
        try { const { data } = await api.put(`/admin/orders/${order_number}/tracking`, tracking); setSelected(data.order); toast.success("Tracking saved"); }
        catch (err) { toast.error(apiError(err)); }
    };

    const invoice = (order_number) => {
        const token = localStorage.getItem("elvosh_token");
        fetch(`${BACKEND_URL}/api/orders/${order_number}/invoice`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob()).then((blob) => { const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `ELVOSH-${order_number}.pdf`; a.click(); URL.revokeObjectURL(url); });
    };

    return (
        <div>
            <h1 className="text-2xl font-semibold mb-6">Orders</h1>
            <div className="flex flex-wrap gap-3 mb-4">
                <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex items-center border border-gray-300 rounded bg-white px-3">
                    <Search className="h-4 w-4 text-gray-400" />
                    <input data-testid="order-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order/name/email/phone" className="px-2 py-2 text-sm outline-none w-64" />
                </form>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-gray-300 rounded px-3 text-sm bg-white">
                    <option value="">All statuses</option>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 text-xs uppercase tracking-wider">
                        <tr><th className="text-left p-4">Order</th><th className="text-left p-4">Customer</th><th className="text-left p-4">Total</th><th className="text-left p-4">Payment</th><th className="text-left p-4">Status</th><th className="text-right p-4"></th></tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {orders.length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-gray-400">No orders found.</td></tr> :
                        orders.map((o) => (
                            <tr key={o.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelected(o)} data-testid={`admin-order-${o.order_number}`}>
                                <td className="p-4"><p className="font-medium">{o.order_number}</p><p className="text-xs text-gray-400">{(o.created_at || "").slice(0, 10)}</p></td>
                                <td className="p-4">{o.customer.name}<p className="text-xs text-gray-400">{o.customer.phone}</p></td>
                                <td className="p-4">{formatPrice(o.total)}</td>
                                <td className="p-4"><span className={`text-xs px-2 py-1 rounded ${o.payment.status === "paid" ? "bg-green-100 text-green-700" : o.payment.status === "failed" ? "bg-red-100 text-red-600" : "bg-yellow-100 text-yellow-700"}`}>{o.payment.status}</span></td>
                                <td className="p-4"><span className="text-xs px-2 py-1 rounded bg-gray-100">{o.status}</span></td>
                                <td className="p-4 text-right"><button onClick={(e) => { e.stopPropagation(); invoice(o.order_number); }} className="p-2 hover:bg-gray-100 rounded"><Download className="h-4 w-4" /></button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    {selected && <OrderDetail order={selected} onStatus={updateStatus} onTracking={saveTracking} onInvoice={invoice} />}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function OrderDetail({ order, onStatus, onTracking, onInvoice }) {
    const [tracking, setTracking] = useState(order.tracking || { courier: "", number: "", url: "" });
    useEffect(() => setTracking(order.tracking || { courier: "", number: "", url: "" }), [order.order_number]);
    return (
        <div>
            <DialogHeader><DialogTitle>{order.order_number}</DialogTitle></DialogHeader>
            <div className="space-y-4 text-sm mt-2">
                <div className="grid grid-cols-2 gap-4">
                    <div><p className="text-xs uppercase text-gray-500 mb-1">Customer</p><p>{order.customer.name}</p><p>{order.customer.phone}</p><p>{order.customer.email}</p></div>
                    <div><p className="text-xs uppercase text-gray-500 mb-1">Delivery Address</p><p>{order.address.line}</p><p>{order.address.city}, {order.address.state} - {order.address.pincode}</p></div>
                </div>
                <div>
                    <p className="text-xs uppercase text-gray-500 mb-1">Items</p>
                    {order.items.map((it, i) => <div key={i} className="flex justify-between border-b border-gray-100 py-1"><span>{it.name} × {it.qty}</span><span>{formatPrice(it.line_total)}</span></div>)}
                    <div className="flex justify-between font-semibold pt-2"><span>Total</span><span>{formatPrice(order.total)}</span></div>
                    {order.coupon_code && <p className="text-xs text-gold">Coupon {order.coupon_code}: −{formatPrice(order.coupon_discount)}</p>}
                </div>
                <div className="grid grid-cols-2 gap-4 bg-gray-50 p-3 rounded">
                    <div><p className="text-xs text-gray-500">Payment Method</p><p>{order.payment.method}</p></div>
                    <div><p className="text-xs text-gray-500">Payment Status</p><p>{order.payment.status}</p></div>
                    {order.payment.razorpay_order_id && <div><p className="text-xs text-gray-500">RZP Order ID</p><p className="text-xs">{order.payment.razorpay_order_id}</p></div>}
                    {order.payment.razorpay_payment_id && <div><p className="text-xs text-gray-500">RZP Payment ID</p><p className="text-xs">{order.payment.razorpay_payment_id}</p></div>}
                </div>
                <div>
                    <p className="text-xs uppercase text-gray-500 mb-1">Update Status</p>
                    <select data-testid="order-status-select" value={order.status} onChange={(e) => onStatus(order.order_number, e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2">
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
                <div>
                    <p className="text-xs uppercase text-gray-500 mb-1">Tracking</p>
                    <div className="grid grid-cols-3 gap-2">
                        <input placeholder="Courier" value={tracking.courier} onChange={(e) => setTracking({ ...tracking, courier: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5" />
                        <input placeholder="Tracking No" value={tracking.number} onChange={(e) => setTracking({ ...tracking, number: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5" />
                        <input placeholder="Tracking URL" value={tracking.url} onChange={(e) => setTracking({ ...tracking, url: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5" />
                    </div>
                    <button onClick={() => onTracking(order.order_number, tracking)} className="mt-2 bg-ink text-white px-4 py-2 rounded text-xs">Save Tracking</button>
                </div>
                <button onClick={() => onInvoice(order.order_number)} className="flex items-center gap-2 text-gold text-sm"><Download className="h-4 w-4" /> Download Invoice PDF</button>
            </div>
        </div>
    );
}
